import type { CSSProperties } from 'react';
import { useEffect, useMemo, useState } from 'react';

import { ChevronDown, ChevronRight } from 'lucide-react';
import { matchPath, NavLink, useLocation } from 'react-router-dom';

import { canonicalOpsRoutePath } from '@/features/navigation/opsMenuPaths';

import type { MenuTreeNode } from '../types/menu.types';
import { menuIconFromString } from '../utils/menuIconMap';
import { getOpsSidebarLabel } from '../utils/opsMenuLabels';

const linkBase: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '10px 12px',
  borderRadius: 8,
  fontSize: 12,
  fontWeight: 600,
  textDecoration: 'none',
};

function sortActiveNodes(nodes: MenuTreeNode[]): MenuTreeNode[] {
  return [...nodes].filter((n) => n.is_active).sort((a, b) => a.sort_order - b.sort_order);
}

function matchesMenuLeaf(
  pathname: string,
  rawPath: string | null | undefined,
  depth: number,
): boolean {
  if (!rawPath || rawPath.startsWith('http')) return false;
  const pattern = canonicalOpsRoutePath(rawPath);
  return matchPath({ path: pattern, end: depth === 0, caseSensitive: true }, pathname) != null;
}

function isDescendantPathActive(node: MenuTreeNode, pathname: string, depth: number): boolean {
  const children = sortActiveNodes(node.children ?? []);
  if (children.length === 0) {
    return matchesMenuLeaf(pathname, node.path, depth);
  }
  return children.some((c) => isDescendantPathActive(c, pathname, depth + 1));
}

function OpsMenuBranch({ nodes, depth }: { nodes: MenuTreeNode[]; depth: number }) {
  const sorted = sortActiveNodes(nodes);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: depth > 0 ? 2 : 4 }}>
      {sorted.map((node) => (
        <OpsMenuNode key={node.id} node={node} depth={depth} />
      ))}
    </div>
  );
}

function OpsMenuNode({ node, depth }: { node: MenuTreeNode; depth: number }) {
  const location = useLocation();
  const label = getOpsSidebarLabel(node);
  const children = sortActiveNodes(node.children ?? []);

  const subtreeActive = useMemo(
    () => isDescendantPathActive(node, location.pathname, depth),
    [node, location.pathname, depth],
  );

  const [open, setOpen] = useState(subtreeActive);

  useEffect(() => {
    const next = subtreeActive;
    let cancelled = false;
    queueMicrotask(() => {
      if (!cancelled) setOpen(next);
    });
    return () => {
      cancelled = true;
    };
  }, [subtreeActive]);

  if (children.length > 0) {
    const branchInactive = {
      color: 'var(--text-secondary)' as const,
      background: 'transparent' as const,
      border: '1px solid transparent' as const,
    };
    const chevron = open ? (
      <ChevronDown size={14} color="var(--text-secondary)" strokeWidth={2} aria-hidden />
    ) : (
      <ChevronRight size={14} color="var(--text-secondary)" strokeWidth={2} aria-hidden />
    );
    const subMenuId = `ops-menu-children-${node.id}`;

    return (
      <div>
        <button
          type="button"
          className="ops-menu-branch-trigger"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls={open ? subMenuId : undefined}
          style={{
            ...linkBase,
            ...branchInactive,
            width: '100%',
            boxSizing: 'border-box',
            paddingLeft: 12 + depth * 8,
            cursor: 'pointer',
            textAlign: 'left' as const,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            fontSize: 12,
            fontWeight: 600,
            lineHeight: 1.25,
            fontFamily: 'var(--font-sans, "Noto Sans", system-ui, sans-serif)',
          }}
        >
          {menuIconFromString(node.icon)}
          <span
            style={{
              flex: 1,
              minWidth: 0,
              fontSize: 12,
              fontWeight: 600,
              lineHeight: 1.25,
            }}
          >
            {label}
          </span>
          {chevron}
        </button>
        {open && (
          <div
            id={`ops-menu-children-${node.id}`}
            style={{ marginTop: 4, paddingLeft: depth > 0 ? 4 : 0 }}
            role="region"
            aria-label={label}
          >
            <OpsMenuBranch nodes={children} depth={depth + 1} />
          </div>
        )}
      </div>
    );
  }

  const pad = { ...linkBase, paddingLeft: 12 + depth * 8 };
  const rawPath = node.path;
  if (!rawPath?.trim()) {
    return null;
  }

  if (rawPath.startsWith('http')) {
    return (
      <a
        href={rawPath}
        rel="noreferrer"
        style={{
          ...pad,
          color: 'var(--text-secondary)',
          border: '1px solid transparent',
        }}
      >
        {menuIconFromString(node.icon)}
        {label}
      </a>
    );
  }

  const internalTo = canonicalOpsRoutePath(rawPath);

  return (
    <NavLink
      to={internalTo}
      end={depth === 0}
      style={({ isActive }) => ({
        ...pad,
        color: isActive ? 'var(--neon-cyan)' : 'var(--text-secondary)',
        background: isActive ? 'rgba(6, 182, 212, 0.12)' : 'transparent',
        border: isActive ? '1px solid rgba(6, 182, 212, 0.35)' : '1px solid transparent',
      })}
    >
      {menuIconFromString(node.icon)}
      {label}
    </NavLink>
  );
}

/**
 * Sidebar điều hướng từ `GET /api/menus/me` — chỉ nhánh Vận hành (`/ops` hoặc `metadata.nav_scope`).
 */
export function OpsDynamicMenuNav({ tree }: { tree: MenuTreeNode[] }) {
  if (!tree.length) return null;
  return (
    <nav style={{ display: 'flex', flexDirection: 'column', gap: 4 }} aria-label="Operations menu">
      <OpsMenuBranch nodes={tree} depth={0} />
    </nav>
  );
}
